// Phase 9.A — Operations Agenda ranking. Pure + tested.
//
// Governs the calm home screen under the Calm Operations Doctrine:
//  D5 exactly one primary focus
//  D1 "Needs you" capped at 5 (1 primary + ≤4 secondary), rest = a count
//  D3 calm by default — an all-healthy fleet yields NO needs-you items
//     and the primary focus is the explicit "All loads on track" rest state.

import type { AgentState, AgentStateLabel } from "../tma/agent-state";

export interface AgendaItem {
  loadId: string;
  reference: string;
  route: string; // "Dallas, TX → Atlanta, GA"
  customerName: string;
  agentState: AgentState;
  label: AgentStateLabel;
  /** Higher = more at risk. Used only to order within a priority tier. */
  riskScore: number;
  /** One-line framed decision when label is "Decision needed". */
  decisionLine?: string;
  /** "Handling it · next: …" calm line for handling/resolved states. */
  handlingLine?: string;
  /**
   * Phase 10 — calm "why" from a SURFACED (graduated-path) projection.
   * Absent while Phase 10 is in shadow; the card degrades cleanly (D-3).
   */
  why?: string;
}

// Priority tiers. Only the top tiers are "needs you"; the rest are
// quietly "watching" (D3 — calm by default).
const NEEDS_YOU_PRIORITY: Partial<Record<AgentState, number>> = {
  escalated: 0,
  needs_approval: 1,
  blocked: 2,
};

export interface OperationsAgenda {
  /** D5: exactly one. Null-object rest state, never empty. */
  primaryFocus:
    | { kind: "decision"; item: AgendaItem }
    | { kind: "all_clear"; activeCount: number };
  /** D1: up to 4 (primary is separate); total on screen ≤ 5. */
  secondary: AgendaItem[];
  /** D1: overflow is a calm number, never DOM rows. */
  queuedCount: number;
  /** D3: how many loads the agent is quietly handling, no action needed. */
  watchingCount: number;
  /** D4: one digest number, never a stream. */
  handledCount: number;
}

const SECONDARY_CAP = 4; // + 1 primary = D1's hard cap of 5

/**
 * Rank composed per-load items into the calm agenda. Pure. An
 * all-healthy fleet returns the "all_clear" primary and zero secondary —
 * that quiet result is correct, not a missing feature.
 */
export function buildAgenda(args: {
  items: AgendaItem[];
  activeCount: number;
  handledCount: number;
}): OperationsAgenda {
  const needsYou = args.items
    .filter((i) => i.agentState in NEEDS_YOU_PRIORITY)
    .sort((a, b) => {
      const pa = NEEDS_YOU_PRIORITY[a.agentState] ?? 99;
      const pb = NEEDS_YOU_PRIORITY[b.agentState] ?? 99;
      if (pa !== pb) return pa - pb;
      return b.riskScore - a.riskScore;
    });

  const watchingCount = args.items.length - needsYou.length;

  if (needsYou.length === 0) {
    return {
      primaryFocus: { kind: "all_clear", activeCount: args.activeCount },
      secondary: [],
      queuedCount: 0,
      watchingCount,
      handledCount: args.handledCount,
    };
  }

  const [primary, ...rest] = needsYou;
  return {
    primaryFocus: { kind: "decision", item: primary },
    secondary: rest.slice(0, SECONDARY_CAP),
    queuedCount: Math.max(0, rest.length - SECONDARY_CAP),
    watchingCount,
    handledCount: args.handledCount,
  };
}
